import React, { ComponentType, FunctionComponent, ReactElement, useEffect, useState } from 'react'

import ReactDatePicker from 'react-datepicker'

import 'react-datepicker/dist/react-datepicker.css'

import { enGB, lt, lv, nl, pl, ru } from 'date-fns/locale'

import CalendarIcon from '@/atoms/icons/calendar.svg'
import {
  DATE_DISPLAY_FORMAT,
  DATE_INPUT_FORMAT,
  DATE_INPUT_MASK,
  DATE_PLACEHOLDER,
  DATE_RANGE_INPUT_FORMAT,
  DATE_RANGE_INPUT_MASK,
  DATE_RANGE_PLACEHOLDER,
  getMaxDate,
  getMinDate,
  getValidDate,
} from '@/helpers/date'
import { AdornmentPosition } from '@/models/common'
import i18n from '@/translations'

import { DatepickerHeader, DatepickerHeaderProps, StyledDatePicker } from './atoms'
import PatternInput from './pattern-input'

const locales = {
  lt,
  lv,
  nl,
  en: enGB,
  ru,
  pl,
}

interface Props {
  id: string
  hasError?: boolean
  maxDate?: Date | string
  minDate?: Date | string
  startDate?: Date | string
  endDate?: Date | string
  selected?: Date | string
  onChange?: (date: Date | { from: Date; to: Date }) => void
  selectsRange?: boolean
  disabled?: boolean
  customInput?: ComponentType
  withIconRight?: boolean
}

const DatePickerComponent: FunctionComponent<Props> = ({
  maxDate: initialMaxDate,
  minDate: initialMinDate,
  startDate,
  endDate,
  onChange,
  id,
  hasError,
  selectsRange = false,
  disabled = false,
  customInput,
  withIconRight,
}) => {
  const [start, setStartDate] = useState(getValidDate(startDate))
  const [end, setEndDate] = useState(getValidDate(endDate))
  const minDate = getMinDate(initialMinDate)
  const maxDate = getMaxDate(initialMaxDate)

  useEffect(() => {
    setStartDate(getValidDate(startDate))
    setEndDate(getValidDate(endDate))
  }, [startDate, endDate])

  const getCustomHeader = (props: DatepickerHeaderProps): ReactElement => (
    <DatepickerHeader params={props} locale={locales[i18n.language]} minDate={minDate} maxDate={maxDate} />
  )

  const handleChangeDate = (date: Date | Array<Date>): void => {
    if (selectsRange) {
      const [fromDate, toDate] = date as Array<Date>
      setStartDate(fromDate)
      setEndDate(toDate)
      onChange({ from: fromDate, to: toDate })

      return
    }

    setStartDate(date as Date)
    onChange(date as Date)
  }

  const placeholder = selectsRange ? DATE_RANGE_PLACEHOLDER : DATE_PLACEHOLDER
  const inputFormat = selectsRange ? DATE_RANGE_INPUT_FORMAT : DATE_INPUT_FORMAT
  const inputMask = selectsRange ? DATE_RANGE_INPUT_MASK : DATE_INPUT_MASK

  const customInputElement = (
    <PatternInput
      id={id}
      error={hasError}
      adornment={<CalendarIcon />}
      adornmentPosition={AdornmentPosition.START}
      format={inputFormat}
      mask={inputMask}
      customInput={customInput}
      withIconRight={withIconRight}
    />
  )

  if (selectsRange) {
    return (
      <StyledDatePicker>
        <ReactDatePicker
          placeholderText={placeholder}
          dateFormat={DATE_DISPLAY_FORMAT}
          showPopperArrow={false}
          selectsRange
          disabled={disabled}
          startDate={start}
          endDate={end}
          onChange={handleChangeDate}
          customInput={customInputElement}
          locale={locales[i18n.language]}
          maxDate={maxDate}
          minDate={minDate}
          renderCustomHeader={getCustomHeader}
          selected={start}
          isClearable
          popperPlacement="bottom"
          popperProps={{ strategy: 'fixed' }}
        />
      </StyledDatePicker>
    )
  }

  return (
    <StyledDatePicker>
      <ReactDatePicker
        placeholderText={placeholder}
        dateFormat={DATE_DISPLAY_FORMAT}
        showPopperArrow={false}
        disabled={disabled}
        startDate={start}
        endDate={end}
        onChange={handleChangeDate}
        customInput={customInputElement}
        locale={locales[i18n.language]}
        maxDate={maxDate}
        minDate={minDate}
        renderCustomHeader={getCustomHeader}
        selected={start}
        popperPlacement="bottom"
        popperProps={{ strategy: 'fixed' }}
      />
    </StyledDatePicker>
  )
}

export default DatePickerComponent
